import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeftIcon,
  PencilIcon,
  ShareIcon,
  TrashIcon,
  MapPinIcon,
  ClockIcon,
  ChartBarIcon,
  FlagIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  PlayIcon,
  PauseIcon,
  PhotoIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { theme } from '../../styles/theme';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import ShareTripModal from './ShareTripModal';
import TripMap from './TripMap';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { formatSpeed, formatDistance, formatFuel, formatDuration, resolveTripPreferenceUnits } from '../../utils/unitConversions';
import { resolveAlternateGpsTripId, routeHasGpsData, resolveEffectiveTripTrack } from '../../utils/tripGpsResolve';
import { formatTripEngineHours, getTripRunTimeMinutes, resolveTripDisplayStats, isTripWallClockMinutes } from '../../utils/tripTrackStats';

function parseHealthReport(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : { summary: raw };
    } catch {
      return { summary: raw };
    }
  }
  return typeof raw === 'object' ? raw : null;
}

function asStringList(value) {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value.trim()) return [value];
  return [];
}

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  background: ${theme.colors.background.primary};
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  padding: 0.5rem 1rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.main};
  }
`;

const TripTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const TripMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background: #dcfce7;
          color: #166534;
        `;
      case 'completed':
        return `
          background: #e0f2fe;
          color: #0c4a6e;
        `;
      case 'paused':
        return `
          background: #fef3c7;
          color: #92400e;
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
        `;
    }
  }}
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
  
  ${props => {
    if (props.variant === 'primary') {
      return `
        background: ${theme.colors.primary.main};
        color: white;
        border: none;
        
        &:hover {
          background: ${theme.colors.primary.dark};
        }
      `;
    } else if (props.variant === 'danger') {
      return `
        background: ${theme.colors.error.main};
        color: white;
        border: none;
        
        &:hover {
          background: ${theme.colors.error.dark};
        }
      `;
    } else {
      return `
        background: ${theme.colors.background.paper};
        color: ${theme.colors.text.primary};
        border: 1px solid ${theme.colors.border.primary};
        
        &:hover {
          border-color: ${theme.colors.primary.main};
          color: ${theme.colors.primary.main};
        }
      `;
    }
  }}
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Card = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.5rem;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1rem;
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #60a5fa;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const WaypointsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const WaypointItem = styled.div`
  display: flex;
  align-items: start;
  gap: 1rem;
  padding: 1rem;
  background: ${(p) => (p.$selected ? 'rgba(245, 158, 11, 0.12)' : theme.colors.background.tertiary)};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${(p) => (p.$selected ? theme.colors.warning.main : theme.colors.border.primary)};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.warning.main};
  }
`;

const WaypointIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: ${theme.colors.primary.main};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
`;

const WaypointContent = styled.div`
  flex: 1;
`;

const WaypointTitle = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0 0 0.25rem 0;
`;

const WaypointMeta = styled.div`
  font-size: 0.8125rem;
  color: ${theme.colors.text.secondary};
`;

const Description = styled.div`
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  padding: 1rem;
  background: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  border-left: 3px solid ${theme.colors.primary.main};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${theme.colors.text.secondary};
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  color: ${theme.colors.text.secondary};
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
`;

const PhotoItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${theme.colors.border.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UploadPhotoButton = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 2rem;
  border: 2px dashed ${props => props.dragActive ? theme.colors.primary.main : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.dragActive ? theme.colors.background.card : theme.colors.background.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.dragActive ? theme.colors.primary.main : theme.colors.text.secondary};
  position: relative;
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    background: ${theme.colors.background.card};
    color: ${theme.colors.primary.main};
    transform: translateY(-2px);
  }
  
  input {
    display: none;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: scale(1.1);
  }
`;

const DragOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${theme.colors.primary.main};
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  ${props => props.show && `
    opacity: 0.9;
  `}
`;

const PhotoActions = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  ${PhotoItem}:hover & {
    opacity: 1;
  }
`;

const PhotoActionButton = styled.button`
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.full};
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
  
  &.primary {
    background: rgba(59, 130, 246, 0.9);
    
    &:hover {
      background: rgba(59, 130, 246, 1);
    }
  }
  
  &.delete {
    background: rgba(239, 68, 68, 0.9);
    
    &:hover {
      background: rgba(220, 38, 38, 1);
    }
  }
`;

const DeletePhotoButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.full};
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  ${PhotoItem}:hover & {
    opacity: 1;
  }
  
  &:hover {
    background: rgba(220, 38, 38, 1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.md};
  
  &:hover {
    background: ${theme.colors.background.tertiary};
    color: ${theme.colors.text.primary};
  }
`;

const ModalBody = styled.div`
  margin-bottom: 2rem;
`;

const WarningBox = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    if (props.variant === 'danger') {
      return `
        background: ${theme.colors.error.main};
        color: white;
        border: none;
        
        &:hover {
          background: ${theme.colors.error.dark};
        }
      `;
    } else {
      return `
        background: ${theme.colors.background.tertiary};
        color: ${theme.colors.text.primary};
        border: 1px solid ${theme.colors.border.primary};
        
        &:hover {
          border-color: ${theme.colors.primary.main};
          color: ${theme.colors.primary.main};
        }
      `;
    }
  }}
`;

const TripDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { preferences } = useUserPreferences();
  const [trip, setTrip] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [trackPoints, setTrackPoints] = useState([]);
  const [selectedTrackIndex, setSelectedTrackIndex] = useState(null);
  const [selectedWaypointIndex, setSelectedWaypointIndex] = useState(null);
  const waypointRefs = useRef({});
  const [derivedStats, setDerivedStats] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [gpsSiblingTripId, setGpsSiblingTripId] = useState(null);
  const [consolidating, setConsolidating] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [waypointRawCount, setWaypointRawCount] = useState(0);
  const [routeSource, setRouteSource] = useState(null);

  const handleWaypointSelect = useCallback((index) => {
    setSelectedWaypointIndex(index);
    const row = waypointRefs.current[index];
    if (row?.scrollIntoView) {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  useEffect(() => {
    loadTripData();
  }, [id]);

  const loadTripData = async () => {
    setLoading(true);
    setMapLoading(true);
    setLoadError(null);
    try {
      const fetchRoute = () => api.get(`/api/trips/${id}/route?telemetry=0&stats=0`, { timeout: 45000 });

      const [tripResult, photosResult] = await Promise.allSettled([
        api.get(`/api/trips/${id}?include_waypoints=true&include_stats=true`, { timeout: 20000 }),
        api.get(`/api/trip-photos/${id}/photos`, { timeout: 10000 }),
      ]);

      let routeResult = await Promise.allSettled([fetchRoute()]).then((r) => r[0]);
      if (routeResult.status === 'rejected') {
        console.warn('Route fetch failed, retrying once:', routeResult.reason);
        routeResult = await Promise.allSettled([fetchRoute()]).then((r) => r[0]);
      }

      if (tripResult.status !== 'fulfilled' || !tripResult.value.data?.trip) {
        const err = tripResult.status === 'rejected' ? tripResult.reason : null;
        console.error('Trip fetch failed:', err);
        const status = err?.response?.status;
        const msg = err?.response?.data?.error || err?.response?.data?.detail || err?.message;
        if (status === 404) {
          setLoadError('This trip was not found. It may have been deleted, or the link may be outdated.');
        } else if (status === 403) {
          setLoadError('You do not have access to this trip.');
        } else {
          setLoadError(msg || 'The server timed out loading this trip. Your GPS data is still stored — try again in a moment.');
        }
        toast.error(msg ? `Failed to load trip: ${msg}` : 'Failed to load trip details');
        return;
      }

      const tripPayload = tripResult.value.data;
      const tripData = tripPayload.trip;
      const route = routeResult.status === 'fulfilled' ? (routeResult.value.data || {}) : {};
      if (routeResult.status === 'rejected') {
        console.warn('Route fetch failed:', routeResult.reason);
      }

      const resolvedId = resolveAlternateGpsTripId(id, tripPayload, route);
      if (resolvedId) {
        toast.success('Your GPS track is on another trip record — opening it now.');
        navigate(`/trips/${resolvedId}`, { replace: true });
        return;
      }

      const siblingId = tripPayload.gps_sibling_trip_id || null;
      setGpsSiblingTripId(siblingId);
      const tripWaypoints = tripPayload.waypoints || [];
      setWaypoints(tripWaypoints);
      setWaypointRawCount(tripPayload.waypoint_count || tripWaypoints.length);

      const tracks = route.track || [];
      const points = route.points || [];
      const { track: effectiveTrack, source: effectiveSource } = resolveEffectiveTripTrack({
        gpsTrack: tracks,
        waypoints: tripWaypoints,
      });

      const computed = resolveTripDisplayStats({
        trip: tripData,
        routeStats: route.stats,
        track: effectiveTrack,
        points,
        waypoints: tripWaypoints,
        routeStartTime: route.start?.time,
      });

      setRouteSource(route.route_source || effectiveSource || (tracks.length > 0 ? 'tracks' : null));
      tripData.track = effectiveTrack.length > 0 ? effectiveTrack : tracks;
      setTrackPoints(points);
      setDerivedStats(computed);
      setTrip({ ...tripData });
      setLoading(false);

      const photos = photosResult.status === 'fulfilled'
        ? (photosResult.value.data.photos || [])
        : [];
      setPhotos(photos);

      const needsRunTimeFix = isTripWallClockMinutes(parseInt(tripData.run_time_minutes, 10), tripData);
      const needsStatsPersist = needsRunTimeFix
        || ((effectiveTrack.length > 1 || computed.distanceKm > 0)
          && parseFloat(tripData.total_distance_km || 0) <= 0);
      if (needsStatsPersist) {
        api.post(`/api/trips/${id}/recalculate-stats`, {}, { timeout: 45000 })
          .then((res) => {
            if (res.data?.stats) {
              setDerivedStats((prev) => resolveTripDisplayStats({
                trip: res.data.trip || tripData,
                routeStats: res.data.stats,
                track: effectiveTrack,
                points,
                waypoints: tripWaypoints,
                routeStartTime: route.start?.time,
              }));
            }
            if (res.data?.trip) {
              setTrip((prev) => ({ ...prev, ...res.data.trip, track: effectiveTrack }));
            }
          })
          .catch(() => {});
      } else if (effectiveTrack.length === 0 && route.route_source !== 'waypoints' && siblingId) {
        setGpsSiblingTripId(siblingId);
      }
    } catch (error) {
      console.error('Error loading trip:', error);
      toast.error(error.response?.data?.error || 'Failed to load trip details');
    } finally {
      setLoading(false);
      setMapLoading(false);
    }
  };

  const handleCompleteTrip = () => {
    setShowCompleteModal(true);
  };

  const confirmCompleteTrip = async () => {
    try {
      await api.put(`/api/trips/${id}`, { trip_status: 'completed' });
      toast.success('Trip completed!');
      loadTripData();
    } catch (error) {
      console.error('Error completing trip:', error);
      toast.error('Failed to complete trip');
    } finally {
      setShowCompleteModal(false);
    }
  };

  const handleEditTrip = () => {
    navigate(`/trips/${id}/edit`);
  };

  const handleShareTrip = () => {
    setShowShareModal(true);
  };

  const handleOpenGpsSibling = () => {
    if (gpsSiblingTripId) {
      navigate(`/trips/${gpsSiblingTripId}`);
    }
  };

  const handleRepairFromWaypoints = async () => {
    try {
      setRepairing(true);
      const res = await api.post(`/api/trips/${id}/repair-from-waypoints`, {}, { timeout: 120000 });
      const removed = res.data?.dedupe?.removed || 0;
      const inserted = res.data?.synth?.inserted || 0;
      toast.success(removed > 0 || inserted > 0
        ? `Trip repaired: removed ${removed.toLocaleString()} duplicate stops, added ${inserted.toLocaleString()} GPS points`
        : 'Trip cleaned up — recalculating stats…');
      setTimeout(() => loadTripData(), 2000);
    } catch (error) {
      console.error('Repair failed:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail ? `Repair failed: ${detail}` : (error.response?.data?.error || 'Failed to repair trip'));
    } finally {
      setRepairing(false);
    }
  };

  const handleConsolidateDuplicates = async () => {
    try {
      setConsolidating(true);
      const res = await api.post(`/api/trips/${id}/consolidate-duplicates`, {
        source_trip_id: gpsSiblingTripId || undefined,
      });
      const moved = (res.data?.movedTracks || 0) + (res.data?.movedLegacy || 0);
      const synthesized = res.data?.synthesized?.inserted || 0;
      const msg = res.data?.message;

      if (moved > 0) {
        toast.success(msg || `Merged ${moved} GPS points from duplicate trip records`);
      } else if (synthesized > 0) {
        toast.success(msg || `Built GPS track from ${synthesized} anchor stops`);
      } else if (res.data?.hasGpsTrack) {
        toast.info(msg || 'GPS is already on this trip — refreshing…');
      } else {
        toast.warning(msg || 'No GPS found to merge. Try "Repair trip from stops" if you have anchor stops.');
      }
      setGpsSiblingTripId(null);
      loadTripData();
    } catch (error) {
      console.error('Consolidate failed:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to merge duplicate trips');
    } finally {
      setConsolidating(false);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success('Trip link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link. Please copy manually: ' + text);
    }
    
    document.body.removeChild(textArea);
  };

  const handleDeleteTrip = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteTrip = async () => {
    try {
      await api.delete(`/api/trips/${id}`, { timeout: 300000 });
      toast.success('Trip deleted');
      navigate('/trips');
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error(error.response?.data?.error || 'Failed to delete trip');
    } finally {
      setShowDeleteModal(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePhotoUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('photos', file);
      });
      formData.append('trip_id', id);

      const response = await api.post(`/api/trip-photos/${id}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const newPhotos = response.data.photos;
      setPhotos([...photos, ...newPhotos]);
      
      // Set first uploaded photo as primary if no primary exists
      if (!trip?.primary_image_url && newPhotos.length > 0) {
        await handleSetPrimaryImage(newPhotos[0].url);
      }
      
      toast.success(`${files.length} photo(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const handleSetPrimaryImage = async (imageUrl) => {
    try {
      await api.put(`/api/trips/${id}`, { primary_image_url: imageUrl });
      setTrip({ ...trip, primary_image_url: imageUrl });
      toast.success('Primary image updated');
    } catch (error) {
      console.error('Error setting primary image:', error);
      toast.error('Failed to set primary image');
    }
  };

  const handleFileInput = (event) => {
    handlePhotoUpload(event.target.files);
    event.target.value = ''; // Reset input
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files).filter(file => 
        file.type.startsWith('image/')
      );
      
      if (files.length > 0) {
        handlePhotoUpload(files);
      } else {
        toast.error('Please drop only image files');
      }
    }
  };

  const handleDeletePhoto = (photoId) => {
    setPhotoToDelete(photoId);
  };

  const confirmDeletePhoto = async () => {
    if (!photoToDelete) return;

    try {
      await api.delete(`/api/trip-photos/${id}/photos/${photoToDelete}`);
      setPhotos(photos.filter(p => p.id !== photoToDelete));
      toast.success('Photo deleted');
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    } finally {
      setPhotoToDelete(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Loading trip details...</LoadingState>
      </PageContainer>
    );
  }

  if (!trip) {
    return (
      <PageContainer>
        <EmptyState>
          <h2>{loadError ? 'Could not load trip' : 'Trip not found'}</h2>
          <p>{loadError || "The trip you're looking for doesn't exist."}</p>
          <BackButton to="/trips">Back to Trips</BackButton>
        </EmptyState>
      </PageContainer>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <PlayIcon className="w-3 h-3" />;
      case 'paused':
        return <PauseIcon className="w-3 h-3" />;
      case 'completed':
        return <CheckCircleIcon className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const { track: statsTrack } = resolveEffectiveTripTrack({
    gpsTrack: trip.track || [],
    waypoints,
  });

  const liveStats = resolveTripDisplayStats({
    trip,
    routeStats: derivedStats,
    track: statsTrack,
    points: trackPoints,
    waypoints,
    routeStartTime: trip.start_time,
  });

  const displayDistanceKm = parseFloat(trip.total_distance_km || 0) > 0
    ? parseFloat(trip.total_distance_km)
    : (liveStats.distanceKm || 0);
  const displayDurationMinutes = getTripRunTimeMinutes(trip, liveStats);
  const elapsedOpenMinutes = trip.start_time
    ? Math.max(0, Math.round((
      (trip.end_time ? new Date(trip.end_time) : new Date()) - new Date(trip.start_time)
    ) / 60000))
    : 0;
  let displayAvgSpeedKnots = parseFloat(trip.average_speed_knots || 0) > 0
    ? parseFloat(trip.average_speed_knots)
    : (liveStats.avgSpeedKnots || 0);
  let displayMaxSpeedKnots = parseFloat(trip.max_speed_knots || 0) > 0
    ? parseFloat(trip.max_speed_knots)
    : (liveStats.maxSpeedKnots || 0);
  if (displayAvgSpeedKnots <= 0 && displayDurationMinutes > 0 && displayDistanceKm > 0) {
    displayAvgSpeedKnots = (displayDistanceKm * 0.539957) / (displayDurationMinutes / 60);
  }
  const displayEngineHours = formatTripEngineHours(
    trip.total_engine_hours,
    liveStats.engineHoursDelta
  );
  const storedFuel = parseFloat(trip.total_fuel_used_liters || 0) || 0;
  const displayFuelLiters = storedFuel > 0
    ? storedFuel
    : (parseFloat(liveStats?.fuelUsed || 0) || 0);
  const healthReport = parseHealthReport(trip.health_report);
  const healthIssues = asStringList(healthReport?.issues);
  const healthRecommendations = asStringList(healthReport?.recommendations);

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <BackButton to="/trips">
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Trips
          </BackButton>
          <div>
            <TripTitle>{trip.trip_name}</TripTitle>
            <TripMeta>
              <StatusBadge status={trip.trip_status}>
                {getStatusIcon(trip.trip_status)}
                {trip.trip_status}
              </StatusBadge>
              <span>🚢 {trip.vessel_name}</span>
              <span>📅 {formatDate(trip.start_time)}</span>
            </TripMeta>
          </div>
        </HeaderLeft>
        
        <Actions>
          {trip.trip_status === 'active' && (
            <ActionButton variant="primary" onClick={handleCompleteTrip}>
              <FlagIcon className="w-5 h-5" />
              Complete Trip
            </ActionButton>
          )}
          <ActionButton 
            variant="primary" 
            onClick={() => navigate(`/trips/${trip.id}/map`)}
            style={{ backgroundColor: theme.colors.secondary.main }}
          >
            <MapPinIcon className="w-5 h-5" />
            View Map & Analytics
          </ActionButton>
          <ActionButton onClick={handleEditTrip}>
            <PencilIcon className="w-5 h-5" />
            Edit
          </ActionButton>
          <ActionButton onClick={handleShareTrip}>
            <ShareIcon className="w-5 h-5" />
            Share
          </ActionButton>
          <ActionButton variant="danger" onClick={handleDeleteTrip}>
            <TrashIcon className="w-5 h-5" />
            Delete
          </ActionButton>
        </Actions>
      </Header>

      {waypointRawCount > 50 && (!trip.track || trip.track.length === 0) && (
        <div style={{
          marginBottom: '1rem',
          padding: '1rem 1.25rem',
          borderRadius: theme.borderRadius.md,
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.35)',
          color: theme.colors.text.primary,
          lineHeight: 1.5,
        }}>
          <strong>This trip has {waypointRawCount.toLocaleString()} auto-detected stops but no GPS track.</strong>
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: theme.colors.text.secondary }}>
            Stop detection fired too often while you were at anchor. Your position data is still here — repair will dedupe stops, rebuild the route, and recalculate stats.
          </div>
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <ActionButton variant="primary" onClick={handleRepairFromWaypoints} disabled={repairing}>
              {repairing ? 'Repairing…' : 'Repair trip from stops'}
            </ActionButton>
          </div>
        </div>
      )}

      {gpsSiblingTripId && !trip.has_gps_track && (
        <div style={{
          marginBottom: '1rem',
          padding: '1rem 1.25rem',
          borderRadius: theme.borderRadius.md,
          background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid rgba(245, 158, 11, 0.4)',
          color: theme.colors.text.primary,
          lineHeight: 1.5,
        }}>
          <strong>GPS data is on a duplicate trip record.</strong>
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: theme.colors.text.secondary }}>
            This trip has no GPS track, but another &ldquo;{trip.trip_name}&rdquo; record has your route data.
          </div>
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <ActionButton variant="primary" onClick={handleOpenGpsSibling}>
              Open trip with GPS
            </ActionButton>
            <ActionButton onClick={handleConsolidateDuplicates} disabled={consolidating}>
              {consolidating ? 'Merging…' : 'Merge GPS into this trip'}
            </ActionButton>
          </div>
        </div>
      )}

      <ContentGrid>
        <MainColumn>
          {/* Trip Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>
                <ChartBarIcon className="w-5 h-5" />
                Trip Statistics
              </CardTitle>
            </CardHeader>
            {routeSource === 'waypoints' && displayDistanceKm > 0 && (
              <div style={{ padding: '0 1.5rem', fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                Distance from anchor stops until GPS track loads or is merged into this trip.
              </div>
            )}
            
            <StatsGrid>
              <StatItem>
                <StatValue>{displayDurationMinutes > 0 ? formatDuration(displayDurationMinutes) : '0m'}</StatValue>
                <StatLabel>Run Time (underway)</StatLabel>
              </StatItem>
              {elapsedOpenMinutes > displayDurationMinutes + 30 && (
                <StatItem>
                  <StatValue>{formatDuration(elapsedOpenMinutes)}</StatValue>
                  <StatLabel>Time away from slip</StatLabel>
                </StatItem>
              )}
              
              <StatItem>
                <StatValue>
                  {preferences ? formatDistance(displayDistanceKm / 1.852, preferences.distance_unit, 1) : `${displayDistanceKm.toFixed(1)} km`}
                </StatValue>
                <StatLabel>
                  Distance ({preferences ? (preferences.distance_unit === 'kilometers' ? 'km' : preferences.distance_unit === 'statute_miles' ? 'mi' : 'nm') : 'km'})
                </StatLabel>
              </StatItem>
              
              
              <StatItem>
                <StatValue>
                  {preferences ? formatSpeed(displayAvgSpeedKnots, preferences.speed_unit, 1) : `${displayAvgSpeedKnots.toFixed(1)} kts`}
                </StatValue>
                <StatLabel>
                  Avg Speed ({preferences ? (preferences.speed_unit === 'mph' ? 'mph' : preferences.speed_unit === 'kmh' ? 'km/h' : 'kts') : 'kts'})
                </StatLabel>
              </StatItem>
              
              <StatItem>
                <StatValue>
                  {preferences ? formatSpeed(displayMaxSpeedKnots, preferences.speed_unit, 1) : `${displayMaxSpeedKnots.toFixed(1)} kts`}
                </StatValue>
                <StatLabel>
                  Max Speed ({preferences ? (preferences.speed_unit === 'mph' ? 'mph' : preferences.speed_unit === 'kmh' ? 'km/h' : 'kts') : 'kts'})
                </StatLabel>
              </StatItem>
              
              <StatItem>
                <StatValue>
                  {preferences ? formatFuel(displayFuelLiters, preferences.fuel_units, 1) : `${displayFuelLiters.toFixed(1)} L`}
                </StatValue>
                <StatLabel>
                  Fuel Used ({preferences ? (preferences.fuel_units === 'gallons' ? 'gal' : preferences.fuel_units === 'imperial_gallons' ? 'imp gal' : 'L') : 'L'})
                </StatLabel>
              </StatItem>
              
              <StatItem>
                <StatValue>{displayEngineHours}</StatValue>
                <StatLabel>Engine Hours</StatLabel>
              </StatItem>
              
              <StatItem>
                <StatValue>
                  {waypointRawCount > waypoints.length
                    ? `${waypoints.length}+`
                    : waypoints.length}
                </StatValue>
                <StatLabel>
                  Stops{waypointRawCount > waypoints.length ? ` (${waypointRawCount.toLocaleString()} raw)` : ''}
                </StatLabel>
              </StatItem>
            </StatsGrid>
          </Card>

          {healthReport && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <CheckCircleIcon className="w-5 h-5" />
                  Vessel Health Analysis
                </CardTitle>
              </CardHeader>
              <div style={{ padding: '0 1.5rem 1.5rem' }}>
                <p style={{ margin: '0 0 1rem', color: theme.colors.text.secondary, lineHeight: 1.5 }}>
                  {healthReport.summary || 'Health analysis unavailable'}
                </p>
                {healthIssues.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: '#f59e0b' }}>Items to review</strong>
                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                      {healthIssues.map((issue, i) => (
                        <li key={i} style={{ marginBottom: '0.35rem' }}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {healthRecommendations.length > 0 && (
                  <div>
                    <strong style={{ color: theme.colors.primary.main }}>Recommendations</strong>
                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                      {healthRecommendations.map((rec, i) => (
                        <li key={i} style={{ marginBottom: '0.35rem' }}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {healthReport.systems?.engines && Object.keys(healthReport.systems.engines).length > 0 && (
                  <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                    Engine telemetry captured on {healthReport.systems.pointsWithEngineData || 0} propulsion samples
                    {healthReport.systems.trackPointsTotal != null && (
                      <> (of {healthReport.systems.trackPointsTotal} GPS points)</>
                    )}
                    {Object.entries(healthReport.systems.engines).map(([key, stats]) => {
                      const delta = stats.hoursDeltaThisTrip != null
                        ? stats.hoursDeltaThisTrip
                        : (stats.hoursEnd != null && stats.hoursStart != null
                          ? stats.hoursEnd - stats.hoursStart
                          : null);
                      const showDelta = delta != null && delta > 0 && delta <= 168;
                      return (
                        <div key={key} style={{ marginTop: '0.35rem' }}>
                          {key}: avg {Math.round(stats.avgRpm || 0)} RPM, max {Math.round(stats.maxRpm || 0)} RPM
                          {showDelta && <> · {delta.toFixed(2)} h this trip</>}
                          {stats.avgFuelLph > 0 && <> · avg {stats.avgFuelLph.toFixed(1)} L/h</>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Map Placeholder */}
          <Card>
            <CardHeader>
              <CardTitle>
                <MapPinIcon className="w-5 h-5" />
                Trip Route
              </CardTitle>
            </CardHeader>
            
            {mapLoading ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: theme.colors.text.secondary }}>
                Loading route…
              </div>
            ) : (
            <TripMap
              track={trip.track || []}
              trackPoints={trackPoints}
              selectedIndex={selectedTrackIndex}
              onPointSelect={setSelectedTrackIndex}
              selectedWaypointIndex={selectedWaypointIndex}
              onWaypointSelect={handleWaypointSelect}
              waypoints={waypoints}
              startPoint={trip.start_latitude && trip.start_longitude ? {
                lat: trip.start_latitude,
                lon: trip.start_longitude,
                name: trip.start_port,
                time: trip.start_time
              } : null}
              endPoint={trip.end_latitude && trip.end_longitude ? {
                lat: trip.end_latitude,
                lon: trip.end_longitude,
                name: trip.end_port,
                time: trip.end_time
              } : null}
              height="600px"
              showStats={true}
              preferenceUnits={resolveTripPreferenceUnits(preferences || {})}
            />
            )}
          </Card>

          {/* Trip Photos */}
          <Card>
            <CardHeader>
              <CardTitle>
                <PhotoIcon className="w-5 h-5" />
                Trip Photos
              </CardTitle>
            </CardHeader>
            
            {/* Upload Area - Always visible */}
            <div style={{ marginBottom: '1.5rem' }}>
              <UploadPhotoButton 
                dragActive={dragActive}
                onDragEnter={handleDragIn}
                onDragLeave={handleDragOut}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input 
                  type="file" 
                  accept="image/*" 
                  multiple 
                  onChange={handleFileInput}
                  disabled={uploading}
                />
                <PlusIcon className="w-8 h-8" />
                <span style={{ fontSize: '0.875rem', marginTop: '0.5rem', fontWeight: 500 }}>
                  {uploading ? 'Uploading...' : 'Click to upload or drag and drop photos'}
                </span>
                <span style={{ fontSize: '0.75rem', marginTop: '0.25rem', opacity: 0.7 }}>
                  PNG, JPG, GIF up to 10MB
                </span>
                <DragOverlay show={dragActive}>
                  <div style={{ fontSize: '2rem' }}>📸</div>
                  <div style={{ fontSize: '1rem', marginTop: '0.5rem', fontWeight: 600 }}>
                    Drop photos here
                  </div>
                </DragOverlay>
              </UploadPhotoButton>
            </div>
            
            {/* Photo Grid */}
            {photos.length > 0 && (
              <PhotoGrid>
              {photos.map((photo) => (
                <PhotoItem key={photo.id}>
                  <img src={photo.url} alt={photo.caption || 'Trip photo'} />
                  <PhotoActions>
                    {trip?.primary_image_url !== photo.url && (
                      <PhotoActionButton 
                        className="primary" 
                        onClick={() => handleSetPrimaryImage(photo.url)}
                        title="Set as primary image"
                      >
                        ★
                      </PhotoActionButton>
                    )}
                    <PhotoActionButton 
                      className="delete" 
                      onClick={() => handleDeletePhoto(photo.id)}
                      title="Delete photo"
                    >
                      ×
                    </PhotoActionButton>
                  </PhotoActions>
                </PhotoItem>
              ))}
              </PhotoGrid>
            )}
            
            {photos.length === 0 && !uploading && (
              <EmptyState style={{ padding: '1rem' }}>
                <PhotoIcon className="w-12 h-12" style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
                <p>No photos yet. Upload some memories from your trip!</p>
              </EmptyState>
            )}
          </Card>

          {/* Waypoints */}
          <Card>
            <CardHeader>
              <CardTitle>
                <MapPinIcon className="w-5 h-5" />
                Stops & Waypoints
              </CardTitle>
            </CardHeader>
            
            {waypoints.length > 0 ? (
              <WaypointsList>
                {waypointRawCount > waypoints.length && (
                  <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                    Showing {waypoints.length} deduplicated stops
                    {waypointRawCount > waypoints.length ? ` (${waypointRawCount.toLocaleString()} were recorded — use Repair to clean up)` : ''}.
                  </p>
                )}
                {waypoints.map((waypoint, index) => {
                  const duration = waypoint.departure_time 
                    ? Math.round((new Date(waypoint.departure_time) - new Date(waypoint.arrival_time)) / 60000)
                    : null;
                  
                  return (
                    <WaypointItem
                      key={waypoint.id}
                      ref={(el) => { waypointRefs.current[index] = el; }}
                      $selected={selectedWaypointIndex === index}
                      onClick={() => handleWaypointSelect(index)}
                      style={{ cursor: 'pointer' }}
                    >
                      <WaypointIcon>{index + 1}</WaypointIcon>
                      <WaypointContent>
                        <WaypointTitle>{waypoint.waypoint_name}</WaypointTitle>
                        <WaypointMeta>
                          {waypoint.location_name && <span>📍 {waypoint.location_name}</span>}
                          {waypoint.arrival_time && (
                            <span> • 🕐 Arrived: {formatDate(waypoint.arrival_time)}</span>
                          )}
                          {duration && <span> • ⏱️ {formatDuration(duration)}</span>}
                          {waypoint.water_depth_meters && (
                            <span> • 🌊 Depth: {waypoint.water_depth_meters.toFixed(1)}m</span>
                          )}
                        </WaypointMeta>
                        {waypoint.notes && (
                          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8125rem', color: theme.colors.text.secondary }}>
                            {waypoint.notes}
                          </p>
                        )}
                      </WaypointContent>
                    </WaypointItem>
                  );
                })}
              </WaypointsList>
            ) : (
              <EmptyState>
                <MapPinIcon className="w-12 h-12" style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
                <p>No waypoints recorded</p>
              </EmptyState>
            )}
          </Card>
        </MainColumn>

        <SideColumn>
          {/* Trip Info */}
          <Card>
            <CardHeader>
              <CardTitle>
                <ClockIcon className="w-5 h-5" />
                Trip Information
              </CardTitle>
            </CardHeader>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>Vessel</StatLabel>
                <div style={{ color: theme.colors.text.primary, fontWeight: '500' }}>
                  {trip.vessel_name}
                </div>
              </div>
              
              <div>
                <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>Start Port</StatLabel>
                <div style={{ color: theme.colors.text.primary, fontWeight: '500' }}>
                  {trip.start_port || 'Unknown'}
                </div>
              </div>
              
              {trip.end_port && (
                <div>
                  <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>End Port</StatLabel>
                  <div style={{ color: theme.colors.text.primary, fontWeight: '500' }}>
                    {trip.end_port}
                  </div>
                </div>
              )}
              
              <div>
                <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>Trip Type</StatLabel>
                <div style={{ color: theme.colors.text.primary, fontWeight: '500', textTransform: 'capitalize' }}>
                  {trip.trip_type}
                </div>
              </div>
              
              {trip.weather_conditions && (
                <div>
                  <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>Weather</StatLabel>
                  <div style={{ color: theme.colors.text.primary, fontWeight: '500' }}>
                    {trip.weather_conditions}
                  </div>
                </div>
              )}
              
              {trip.sea_conditions && (
                <div>
                  <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>Sea Conditions</StatLabel>
                  <div style={{ color: theme.colors.text.primary, fontWeight: '500' }}>
                    {trip.sea_conditions}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Trip Description */}
          {trip.trip_description && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <Description>{trip.trip_description}</Description>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>
                <CalendarDaysIcon className="w-5 h-5" />
                Timeline
              </CardTitle>
            </CardHeader>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>Started</StatLabel>
                <div style={{ color: theme.colors.text.primary, fontWeight: '500' }}>
                  {formatDate(trip.start_time)}
                </div>
              </div>
              
              {trip.end_time && (
                <div>
                  <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>Ended</StatLabel>
                  <div style={{ color: theme.colors.text.primary, fontWeight: '500' }}>
                    {formatDate(trip.end_time)}
                  </div>
                </div>
              )}
              
              <div>
                <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>Run Time</StatLabel>
                <div style={{ color: theme.colors.primary.main, fontWeight: '700', fontSize: '1.125rem' }}>
                  {formatDuration(displayDurationMinutes)}
                </div>
              </div>

              {trip.trip_status === 'active' && elapsedOpenMinutes > displayDurationMinutes && (
                <div>
                  <StatLabel style={{ textAlign: 'left', marginBottom: '0.25rem' }}>Trip Open</StatLabel>
                  <div style={{ color: theme.colors.text.secondary, fontWeight: '500' }}>
                    {formatDuration(elapsedOpenMinutes)}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </SideColumn>
      </ContentGrid>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ModalOverlay onClick={() => setShowDeleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Delete Trip</ModalTitle>
              <CloseButton onClick={() => setShowDeleteModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <p style={{ color: theme.colors.text.primary, marginBottom: '1rem' }}>
                Are you sure you want to delete <strong>"{trip?.trip_name}"</strong>? This action cannot be undone.
              </p>
              
              <WarningBox>
                <h4 style={{ color: '#92400e', margin: '0 0 0.5rem 0', fontSize: '1rem' }}>
                  ⚠️ Important: This will impact your seasonal reporting
                </h4>
                <ul style={{ color: '#92400e', margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                  <li>Total distance and fuel consumption will be recalculated</li>
                  <li>Season summaries and trends will be affected</li>
                  <li>Engine hour tracking will be updated</li>
                  <li>Historical trip statistics will be modified</li>
                </ul>
              </WarningBox>
              
              <p style={{ color: theme.colors.text.secondary, fontSize: '0.875rem', margin: 0 }}>
                Consider editing the trip instead if you just need to make corrections.
              </p>
            </ModalBody>
            
            <ModalActions>
              <ModalButton onClick={() => setShowDeleteModal(false)}>
                Cancel
              </ModalButton>
              <ModalButton variant="danger" onClick={confirmDeleteTrip}>
                Delete Trip
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Complete Trip Confirmation Modal */}
      {showCompleteModal && (
        <ModalOverlay onClick={() => setShowCompleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Complete Trip</ModalTitle>
              <CloseButton onClick={() => setShowCompleteModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <p style={{ color: theme.colors.text.primary, marginBottom: '1rem' }}>
                Mark <strong>"{trip?.trip_name}"</strong> as completed?
              </p>
              <p style={{ color: theme.colors.text.secondary, fontSize: '0.875rem', margin: 0 }}>
                This will finalize all trip statistics and stop automatic GPS tracking.
                You can still edit trip details after completion.
              </p>
            </ModalBody>
            
            <ModalActions>
              <ModalButton onClick={() => setShowCompleteModal(false)}>
                Cancel
              </ModalButton>
              <ModalButton variant="primary" onClick={confirmCompleteTrip}>
                Complete Trip
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Photo Confirmation Modal */}
      {photoToDelete && (
        <ModalOverlay onClick={() => setPhotoToDelete(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Delete Photo</ModalTitle>
              <CloseButton onClick={() => setPhotoToDelete(null)}>
                ×
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <p style={{ color: theme.colors.text.primary, marginBottom: '0' }}>
                Are you sure you want to delete this photo? This action cannot be undone.
              </p>
            </ModalBody>
            
            <ModalActions>
              <ModalButton onClick={() => setPhotoToDelete(null)}>
                Cancel
              </ModalButton>
              <ModalButton variant="danger" onClick={confirmDeletePhoto}>
                Delete Photo
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Share Trip Modal */}
      <ShareTripModal
        tripId={trip?.id}
        tripName={trip?.trip_name}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </PageContainer>
  );
};

export default TripDetailPage;
